/**
 * EthosBoard Company page content. Source: Company mockup.
 * Edit text here, not in the JSX.
 * Photos: import an image and set `photo`; null shows a placeholder.
 */
import companyHero from '../../assets/images/eb/team.png';

export const CO_HERO = {
  eyebrow: 'Company and team',
  title: 'Infylearn Technologies: the company and team behind EthosBoard',
  subtitle:
    'A global learning technology company with offices in Sharjah, UAE and Mumbai, India. We design and deliver AI-augmented digital learning for enterprises worldwide, and we build EthosBoard.',
  tagline: 'Intelligent Learning. Human Impact.',
  photo: companyHero,
  photoCaption: 'Photo: the Infylearn team in the Sharjah or Mumbai office',
  stats: [
    { v: '150+', k: 'projects delivered' },
    { v: '98%', k: 'client retention' },
    { v: '50+', k: 'languages' },
    { v: '21+', k: 'years combined expertise' },
  ],
};

export const CO_STORY = {
  eyebrow: 'Our story',
  title: 'Learning only matters if it changes how people perform at work',
  p1: 'Infylearn Technologies was founded by Feroz Shaikh, the first in his family to start and grow a profitable business. He built the company on one conviction: learning only matters if it changes how people perform at work.',
  p2: 'Today our team brings 21+ years of combined expertise across instructional design, media production and learning technology. We have delivered 150+ projects for enterprises in North America, Europe and the UK, the Middle East, India and APAC. Our client retention rate is 98%, because we treat every client as a long-term partner, not a one-off project.',
};

export const CO_SERVICES = {
  eyebrow: 'What we do',
  title: 'Digital learning that improves performance',
  subtitle: 'Every deliverable is compliant with SCORM, xAPI and cmi5, and tested to work with your LMS or LXP.',
  items: [
    { icon: 'layout', k: 'Custom eLearning and microlearning' },
    { icon: 'eye', k: 'Video-based learning' },
    { icon: 'users', k: 'ILT and VILT' },
    { icon: 'target', k: 'Gamification and scenario-based learning' },
    { icon: 'image', k: 'Immersive learning with AR, VR and XR' },
    { icon: 'bolt', k: 'AI-powered learning experiences' },
    { icon: 'globe', k: 'Translation and localization in 50+ languages' },
  ],
  platformLabel: 'The EthosBoard platform',
  platformTo: '/platform',
};

export const CO_PLATFORM = {
  eyebrow: 'Our workforce readiness platform',
  title: 'Built from what regulated, complex enterprises actually need',
  text: 'EthosBoard is an AI-powered LMS and workforce readiness platform. It helps organizations launch, sell, manage and scale digital learning from a single platform, and it is designed around readiness, not just completions. AI supports authoring, personalization and insight, while your team stays in control of quality.',
  link: 'Explore the EthosBoard platform',
  linkTo: '/platform',
  deploy: 'Shared SaaS · On-premises · Standalone code',
};

export const CO_TEAM = {
  eyebrow: 'Our team',
  title: 'Specialists who work together on every project',
  text: 'Every brief is assigned a dedicated team of an instructional designer, a visual designer and a project manager.',
  leader: {
    tag: 'LEADERSHIP', name: 'Feroz Shaikh', role: 'Founder and CEO',
    bio: 'Sets direction across services, AI innovation and EthosBoard.',
    photo: null, photoCaption: 'Feroz photo',
  },
  roles: [
    { k: 'Instructional designers', d: 'Turn business problems into clear learning journeys, using ADDIE, SAM and backward design.' },
    { k: 'Visual and media designers', d: 'Create the interfaces, animation, video and immersive experiences.' },
    { k: 'Developers and technologists', d: 'Build interactive modules, simulations and the EthosBoard platform.' },
    { k: 'Localization specialists', d: 'Adapt content for 50+ languages through certified partners.' },
    { k: 'Project managers', d: 'One point of contact and full accountability.' },
    { k: 'Business development and client success', d: 'Understand your goals and stay with you after launch.' },
  ],
};

export const CO_PROCESS = {
  eyebrow: 'How we work',
  title: 'Signal, Structure, Ship, Sustain',
  subtitle: 'Scope and timelines are confirmed at kick-off, and standard modules are typically delivered in 2 to 6 weeks.',
  stages: [
    { k: 'Signal', d: 'Understand the problem.' },
    { k: 'Structure', d: 'Shape the learning design.' },
    { k: 'Ship', d: 'Deliver on time.' },
    { k: 'Sustain', d: 'Keep improving with data.', accent: true },
  ],
};

export const CO_AWARDS = {
  eyebrow: 'Recognized for quality',
  title: 'Three 2026 Brandon Hall Group HCM Excellence Awards',
  items: [
    { m: 'Silver', k: 'Best Use of Video for Learning' },
    { m: 'Silver', k: 'Best Competencies and Skill Development' },
    { m: 'Bronze', k: 'Best Unique or Innovative L&D Program' },
  ],
  sectorsEyebrow: 'Who we serve',
  sectorsText: 'Sectors where compliance, safety and accuracy matter most, and where generic content falls short.',
  sectors: ['Healthcare and pharma', 'Retail', 'Aviation', 'Banking and financial services', 'Manufacturing', 'Oil and gas', 'Logistics', 'IT'],
  link: 'See industry packs',
  linkTo: '/industries',
};

/**
 * PLACEHOLDER testimonials, copied from the mockup. They use [bracketed] names.
 * Replace with approved client testimonials (and set photo / logo) before going live.
 */
export const CO_TESTIMONIALS = {
  eyebrow: 'What clients say',
  title: 'Long-term partners, not one-off projects',
  linkBefore: 'Read full ',
  linkLabel: 'client case studies',
  linkTo: '/resources/client-stories',
  items: [
    { industry: 'HEALTHCARE AND PHARMA', headline: 'GxP onboarding rebuilt for four sites', quote: 'Infylearn treated our SOPs like their own. The new onboarding paths were live in weeks, and our last inspection was the calmest we have had.', name: '[Client name]', role: 'Head of Quality Training, [Pharma company]', org: '[Pharma company]', photo: null, logo: null },
    { industry: 'AVIATION', headline: 'Recurrent training with traceable records', quote: 'We finally have one place that shows who is current on human factors and EWIS, and the evidence is ready before the auditor asks.', name: '[Client name]', role: 'Training Manager, [MRO organization]', org: '[MRO organization]', photo: null, logo: null },
    { industry: 'RETAIL', headline: 'Seasonal hires serving customers on day one', quote: 'Short mobile lessons in two languages changed how fast new store staff ramp up. The team also made the content genuinely enjoyable.', name: '[Client name]', role: 'L&D Lead, [Retail group]', org: '[Retail group]', photo: null, logo: null },
    { industry: 'BANKING AND FINANCIAL SERVICES', headline: 'Role-based AML training people remember', quote: 'Scenario practice replaced slide decks. Our relationship managers now talk about red flags in their own words, which is exactly what we wanted.', name: '[Client name]', role: 'Compliance Director, [Bank]', org: '[Bank]', photo: null, logo: null },
  ],
};

export const CO_FAQS = [
  { q: 'What does Infylearn Technologies do?', a: 'Infylearn Technologies designs and delivers AI-augmented digital learning for global enterprises, from custom eLearning to immersive training, and builds the EthosBoard workforce readiness platform.' },
  { q: 'Who founded Infylearn Technologies?', a: 'Infylearn Technologies was founded by Feroz Shaikh, who leads the company as Founder and CEO.' },
  { q: 'Where is Infylearn Technologies based?', a: 'We have offices in Sharjah, UAE (Sharjah Media City Free Zone) and Mumbai, India, and we serve clients across five global regions.' },
  { q: 'What is the relationship between Infylearn Technologies and EthosBoard?', a: 'EthosBoard is an AI-powered LMS and workforce readiness platform created and owned by Infylearn Technologies.' },
  { q: 'How big is the Infylearn Technologies team?', a: 'With a headcount of 100+ seasoned professionals, our team brings 21+ years of combined expertise and has delivered projects across 14+ industry verticals.' },
];

export const CO_CONTACT = {
  title: 'Contact us',
  text: 'Whether you have a fully scoped brief or a half-formed idea, we can help you find the right approach. We reply to every enquiry within 1 business day.',
  demo: 'Book a demo',
  email: 'engage@infylearn.net',
  offices: [
    { city: 'Sharjah, UAE', addr: 'Shams Business Center, Sharjah Media City Free Zone, Al Messaned, Sharjah, UAE' },
    { city: 'Mumbai, India', addr: 'Awfis, 1st Floor, Parinee Cresenzo, C-30, G Block, Bandra Kurla Complex, Bandra East, Mumbai 400051' },
  ],
};